import React, { useState, useEffect } from 'react';
import {
  Plus, Trash2, ChevronRight, Check, AlertCircle, Mail, Loader2, Copy,
  User, Briefcase, ShieldCheck, ArrowLeft, Phone, MapPin, CalendarClock, PieChart, KeyRound, Power,
} from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import { useApp } from '../../context/AppContext';
import apiService from '../../services/api';
import { PasswordPanel } from './PasswordPanel';
import {
  fmtDays, fmtHours, FULL_DAY_HOURS,
} from '../../utils/leaveBalance';

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

// Inputs pick up their focus ring etc. from the .ed-input class (styles in EmployeeProfile)
const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  height: 40,
  padding: '0 12px',
  borderRadius: 10,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  fontSize: 13.5,
  color: 'var(--text)',
  outline: 'none',
};

const labelStyle = {
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--text-2)',
  marginBottom: 6,
  display: 'block',
};

// Map DB role values -> display labels (one vocabulary across the app:
// Employee, Manager, Admin, Super admin). "Manager" isn't picked here: it
// comes from running a department (Settings → Departments).
const ROLE_OPTIONS = [
  {
    value: 'member',
    label: 'Employee',
    desc: 'Books their own leave and sees the calendar. Becomes a Manager when they run a department.',
  },
  {
    value: 'admin',
    label: 'Admin',
    desc: 'Approves anyone, sees sick notes (HR) and changes all settings.',
  },
  {
    value: 'super_admin',
    label: 'Super admin',
    desc: 'Everything an admin can do, plus billing and closing the account.',
  },
];

const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map(r => [r.value, r.label]));

// ─── Sections in the left-hand menu ───
const SECTIONS = [
  { key: 'profile',   label: 'Profile',   icon: User,          hint: 'Name, department and access' },
  { key: 'contact',   label: 'Contact',   icon: Phone,         hint: 'Email, phone and address' },
  { key: 'schedule',  label: 'Schedule',  icon: CalendarClock, hint: 'Working days and hours' },
  { key: 'allowance', label: 'Allowance', icon: PieChart,      hint: 'Policy, balance and adjustments' },
  { key: 'invite',    label: 'Invite',    icon: Mail,          hint: 'Account activation' },
  { key: 'membership', label: 'Membership', icon: Power,       hint: 'Active, deactivated or archived' },
  { key: 'password',  label: 'Password',  icon: KeyRound,      hint: 'Change or reset' },
];

function fmtDate(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return null; }
}

// ─── A person's full profile page. Replaces the side drawer: sections on the
// left, the chosen section on the right, room for two-column forms. Open it
// in place of the team list and pass onBack to return. ───
export function EmployeeProfile({ employee, onBack, initialSection = 'profile' }) {
  const { updateEmployee, currentUser, user } = useApp();
  const me = currentUser ?? user;

  // Password: your own profile, or anyone's if you're an admin. Only a
  // super admin can set a super admin's password (the backend checks too).
  const isSelf       = me?.id != null && me.id === employee.id;
  const myRole       = me?.role ?? me?.userRole;
  const isAdmin      = ['admin', 'super_admin'].includes(myRole);
  const targetRole   = employee.role ?? employee.userRole ?? employee.systemRole;
  const canSetPassword = isSelf || (isAdmin && (targetRole !== 'super_admin' || myRole === 'super_admin'));
  const sections = SECTIONS.filter(s =>
    (s.key !== 'password' || canSetPassword) &&
    // Only admins can deactivate or archive someone, and never themselves
    (s.key !== 'membership' || (isAdmin && !isSelf))
  );

  const [section, setSection] = useState(
    sections.some(s => s.key === initialSection) ? initialSection : 'profile'
  );
  // active | deactivated | archived — changed straight away, not with the save bar
  const [memberStatus, setMemberStatus] = useState(employee.status ?? 'active');

  const [departments,  setDepartments]  = useState([]);
  const [deptsLoaded,  setDeptsLoaded]  = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [approvers,    setApprovers]    = useState([]);
  // Both must be true before the dept select renders its value
  const readyToShow = deptsLoaded && profileLoaded;

  // Map any legacy/unknown role value to a valid DB enum value
  function normaliseRole(raw) {
    const map = {
      member:        'member',
      standard:      'member',
      admin:         'admin',
      administrator: 'admin',
      director:      'admin',
      super_admin:   'super_admin',
    };
    return map[raw] ?? 'member';
  }

  // Derive dept from every possible shape getAllUsers might return
  const initialDeptId =
    employee.departmentId ??
    employee.deptId ??
    employee.departments?.[0]?.departmentId ??
    employee.departmentMembers?.[0]?.departmentId ??
    '';

  const initialDeptName =
    employee.dept ??
    employee.departmentName ??
    employee.departments?.[0]?.departmentName ??
    employee.departmentMembers?.[0]?.department?.name ??
    '';

  const initialRole = normaliseRole(
    employee.role ?? employee.userRole ?? employee.systemRole ?? ''
  );

  const [form, setForm] = useState({
    firstName:       employee.firstName ?? employee.name?.split(' ')[0] ?? '',
    lastName:        employee.lastName  ?? employee.name?.split(' ').slice(1).join(' ') ?? '',
    email:           employee.email           ?? '',
    dept:            initialDeptName,
    deptId:          initialDeptId,
    approver:        employee.approver        ?? '',
    approverId:      employee.approverId      ?? '',
    employmentStart: employee.employmentStart ?? '',
    employmentEnd:   employee.employmentEnd   ?? '',
    dob:             employee.dob             ?? '',
    userRole:        initialRole,
    phone:           employee.phone           ?? '',
    address:         employee.address         ?? '',
  });

  // Load departments list and approvers
  useEffect(() => {
    apiService.getDepartments()
      .then(res => {
        const payload = res?.data ?? res ?? [];
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.departments)
            ? payload.departments
            : [];
        setDepartments(list);
        setDeptsLoaded(true);
      })
      .catch(err => { console.error(err); setDeptsLoaded(true); });

    // Anyone who can approve: admins and department managers
    apiService.getPossibleApprovers()
      .then(res => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setApprovers(list.filter(u => u.id !== employee.id).map(u => ({
          ...u,
          displayName: u.name || u.email,
          hint: u.managerOf?.length ? `Manager, ${u.managerOf.join(', ')}` : u.role === 'super_admin' ? 'Super admin' : 'Admin',
        })));
      })
      .catch(console.error);
  }, [employee.id]);

  // Fetch full profile — merge with existing form state so we never
  // overwrite dept/role with nulls if the profile row doesn't exist yet
  useEffect(() => {
    apiService.getEmployeeProfile(employee.id)
      .then(res => {
        const d = res?.data ?? res;
        if (!d || typeof d !== 'object') return;
        if (d.status) setMemberStatus(d.status);
        setForm(prev => ({
          firstName:       d.firstName ?? prev.firstName,
          lastName:        d.lastName  ?? prev.lastName,
          email:           d.email     ?? prev.email,
          phone:           d.phone     ?? prev.phone,
          address:         d.address   ?? prev.address,
          employmentStart: d.employmentStart ?? prev.employmentStart,
          employmentEnd:   d.employmentEnd   ?? prev.employmentEnd,
          dob:             d.dob             ?? prev.dob,
          approverId:      d.approverId      ?? prev.approverId,
          approver:        d.approver        ?? prev.approver,
          effectiveApprover: d.effectiveApprover ?? null,
          deptId:  d.departmentId ?? prev.deptId,
          dept:    d.dept         ?? prev.dept,
          // d.role = Users enum; d.userRole = free text on EmployeeProfile
          userRole: d.role ? normaliseRole(d.role) : (d.userRole ? normaliseRole(d.userRole) : prev.userRole),
        }));
      })
      .catch(() => {})
      .finally(() => setProfileLoaded(true));
  }, [employee.id]);

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  // ─── Unsaved-changes tracking for Profile and Contact (they share a save) ───
  const [baseline, setBaseline] = useState(null);
  useEffect(() => {
    if (readyToShow && baseline === null) setBaseline(JSON.stringify(form));
  }, [readyToShow]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = baseline !== null && JSON.stringify(form) !== baseline;

  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const handleSave = async () => {
    setSaveState('saving');
    try {
      await apiService.updateEmployeeProfile(employee.id, {
        firstName:       form.firstName,
        lastName:        form.lastName,
        email:           form.email,
        phone:           form.phone,
        address:         form.address,
        departmentId:    form.deptId     || null,
        approverId:      form.approverId || null,
        employmentStart: form.employmentStart || null,
        employmentEnd:   form.employmentEnd   || null,
        dob:             form.dob             || null,
        userRole:        form.userRole,
      });
      updateEmployee?.({ ...employee, ...form, name: `${form.firstName} ${form.lastName}`.trim() });
      setBaseline(JSON.stringify(form));
      setSaveState('saved');
      setTimeout(() => setSaveState(s => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      console.error('Save failed:', e.response?.data ?? e.message ?? e);
      setSaveState('error');
    }
  };
  const discard = () => { if (baseline) setForm(JSON.parse(baseline)); setSaveState('idle'); };

  // Leaving with unsaved edits asks first (back link and closing the tab)
  function handleBack() {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    onBack?.();
  }
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // Start at the top of the page
  useEffect(() => { window.scrollTo({ top: 0 }); }, [employee.id]);

  const displayName = `${form.firstName} ${form.lastName}`.trim() || employee.name || employee.email;
  const started = fmtDate(form.employmentStart);
  const current = sections.find(s => s.key === section) ?? sections[0];
  const usesForm = section === 'profile' || section === 'contact';

  return (
    <div className="ep-page">
      <button type="button" className="ep-back" onClick={handleBack}>
        <ArrowLeft size={16} /> Team
      </button>

      {/* ─── Header ─── */}
      <header className="ep-head">
        <div className="ed-photo" title="Change photo">
          <Avatar employee={employee} size="lg" />
          <div className="ed-photo-overlay"><span>Change<br />photo</span></div>
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h1 className="ep-name">
            {displayName}
            {isSelf && <span className="ep-you">You</span>}
          </h1>
          <div className="ep-meta">
            <span className="ed-chip" style={form.dept ? { background: 'var(--tint)', color: 'var(--accent)' } : undefined}>
              {form.dept || 'No department'}
            </span>
            <span className="ed-chip">{ROLE_LABEL[form.userRole] ?? 'Standard user'}</span>
            {memberStatus !== 'active' && (
              <span className="ed-chip" style={{ background: 'var(--bad-bg)', color: 'var(--bad-text)' }}>
                {memberStatus === 'archived' ? 'Archived' : 'Deactivated'}
              </span>
            )}
            {employee.isEmailVerified === false && (
              <button type="button" className="ed-chip ep-chip-warn" onClick={() => setSection('invite')}>Invite pending</button>
            )}
            {form.email && <span className="ep-meta-text"><Mail size={13} /> {form.email}</span>}
            {started && <span className="ep-meta-text"><CalendarClock size={13} /> Started {started}</span>}
          </div>
        </div>
      </header>

      <div className="ep-body">
        {/* ─── Section menu ─── */}
        <nav className="ep-nav" aria-label="Profile sections">
          {sections.map(s => {
            const Icon = s.icon;
            const showDot = (s.key === 'profile' || s.key === 'contact') && dirty;
            return (
              <button
                key={s.key}
                type="button"
                className="ep-nav-item"
                aria-current={section === s.key ? 'page' : undefined}
                onClick={() => setSection(s.key)}
              >
                <span className="ep-nav-icon"><Icon size={16} /></span>
                <span style={{ minWidth: 0 }}>
                  <span className="ep-nav-label">{s.label}{showDot && <span className="ep-dot" title="Unsaved changes" />}</span>
                  <span className="ep-nav-hint">{s.hint}</span>
                </span>
              </button>
            );
          })}
        </nav>

        {/* ─── Section content ─── */}
        <section className="ep-content" aria-labelledby="ep-section-title">
          <div className="ep-content-head">
            <h2 id="ep-section-title">{current.label}</h2>
            <p>{current.hint}</p>
          </div>

          <div className="ep-content-body">
            {section === 'profile' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
                <Section icon={User} title="Personal details">
                  <div className="ed-grid">
                    <Field label="First name">
                      <input className="ed-input" style={inputStyle} value={form.firstName} onChange={set('firstName')} />
                    </Field>
                    <Field label="Last name">
                      <input className="ed-input" style={inputStyle} value={form.lastName} onChange={set('lastName')} />
                    </Field>
                    <Field label="Date of birth">
                      <input type="date" className="ed-input" style={inputStyle} value={form.dob} onChange={set('dob')} max={new Date().toISOString().split('T')[0]} />
                    </Field>
                  </div>
                </Section>

                <Section icon={Briefcase} title="Work">
                  <div className="ed-grid">
                    <Field label="Department">
                      {readyToShow ? (
                        <select
                          className="ed-input"
                          style={{ ...inputStyle, cursor: 'pointer' }}
                          value={form.deptId ?? ''}
                          onChange={e => {
                            const dept = departments.find(d => String(d.id) === e.target.value);
                            setForm(f => ({ ...f, deptId: e.target.value, dept: dept?.name ?? '' }));
                          }}
                        >
                          <option value="">No department</option>
                          {departments.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                      ) : (
                        <select className="ed-input" style={{ ...inputStyle, cursor: 'wait' }} disabled>
                          <option>Loading…</option>
                        </select>
                      )}
                    </Field>

                    <Field label="Approver">
                      <select
                        className="ed-input"
                        style={{ ...inputStyle, cursor: 'pointer' }}
                        value={form.approverId ?? ''}
                        onChange={e => {
                          const u = approvers.find(x => String(x.id) === e.target.value);
                          setForm(f => ({ ...f, approverId: e.target.value, approver: u?.displayName ?? '' }));
                        }}
                      >
                        {/* Blank = the usual route, named so it matches who actually decides */}
                        <option value="">
                          {form.effectiveApprover && !form.approverId
                            ? `${form.effectiveApprover.name} (${form.effectiveApprover.source === 'department' ? `manages ${form.effectiveApprover.departmentName ?? 'their department'}` : 'company admin'})`
                            : 'Their department manager, or an admin'}
                        </option>
                        {approvers.map(u => (
                          <option key={u.id} value={u.id}>{u.displayName} · {u.hint}</option>
                        ))}
                      </select>
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.4 }}>
                        {form.approverId
                          ? 'Their requests go to this person. Company admins can also approve.'
                          : 'Leave blank to use their department manager. Company admins can always approve.'}
                      </span>
                    </Field>

                    <Field label="Start date">
                      <input type="date" className="ed-input" style={inputStyle} value={form.employmentStart} onChange={set('employmentStart')} />
                    </Field>
                    <Field label="End date">
                      <input type="date" className="ed-input" style={inputStyle} value={form.employmentEnd} onChange={set('employmentEnd')} min={form.employmentStart || undefined} />
                    </Field>
                  </div>
                </Section>

                {/* Role picker — values match DB enum exactly */}
                <Section icon={ShieldCheck} title="Access">
                  <div role="radiogroup" aria-label="Role" className="ep-roles">
                    {ROLE_OPTIONS.map(opt => (
                      <label key={opt.value} className="ed-role" data-checked={form.userRole === opt.value}>
                        <input
                          type="radio"
                          name="userRole"
                          value={opt.value}
                          checked={form.userRole === opt.value}
                          onChange={set('userRole')}
                          style={{ marginTop: 2, accentColor: 'var(--accent)', flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 13.5 }}>
                          <span style={{ color: 'var(--text)', fontWeight: 600 }}>{opt.label}</span>
                          {opt.desc && (
                            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>
                              {opt.desc}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </Section>

              </div>
            )}

            {section === 'contact' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
                <Section icon={Mail} title="Email and phone">
                  <div className="ed-grid">
                    <Field label="Email address">
                      <input type="email" className="ed-input" style={inputStyle} value={form.email} onChange={set('email')} autoComplete="off" />
                    </Field>
                    <Field label="Phone">
                      <input type="tel" className="ed-input" style={inputStyle} value={form.phone} onChange={set('phone')} placeholder="e.g. 07700 900123" />
                    </Field>
                  </div>
                </Section>
                <Section icon={MapPin} title="Address">
                  <Field label="Home address">
                    <textarea
                      className="ed-input"
                      rows={4}
                      style={{ ...inputStyle, height: 'auto', padding: '10px 12px', lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }}
                      value={form.address}
                      onChange={set('address')}
                    />
                  </Field>
                </Section>
              </div>
            )}

            {section === 'schedule'  && <SchedulePanel employee={employee} />}
            {section === 'allowance' && <AllowancePanel employee={employee} />}
            {section === 'invite'    && <InvitePanel employee={employee} />}
            {section === 'membership' && isAdmin && !isSelf && (
              <MembershipSection
                employee={employee}
                status={memberStatus}
                onChange={(st) => { setMemberStatus(st); updateEmployee?.({ ...employee, status: st }); }}
              />
            )}
            {section === 'password'  && canSetPassword && <PasswordPanel employee={employee} isSelf={isSelf} />}
          </div>

          {/* Save bar for Profile and Contact — sticks to the bottom while editing */}
          {usesForm && (
            <div className="ep-savebar" data-dirty={dirty || undefined}>
              <span className="ep-savebar-msg">
                {saveState === 'error'
                  ? <span style={{ color: 'var(--bad-text)', display: 'inline-flex', alignItems: 'center', gap: 6 }}><AlertCircle size={14} /> Couldn't save. Please try again.</span>
                  : saveState === 'saved'
                    ? <span style={{ color: 'var(--ok-text)', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Check size={14} /> Changes saved</span>
                    : dirty ? 'You have unsaved changes' : 'All changes saved'}
              </span>
              {dirty && <button type="button" className="ed-btn" onClick={discard}>Discard</button>}
              <button
                type="button"
                className="ed-btn ed-btn-primary"
                onClick={handleSave}
                disabled={!dirty || saveState === 'saving'}
              >
                {saveState === 'saving'
                  ? <><Loader2 size={14} style={{ animation: 'edSpin 1s linear infinite' }} /> Saving…</>
                  : 'Save changes'}
              </button>
            </div>
          )}
        </section>
      </div>

      <style>{`
        @keyframes edSpin { to { transform: rotate(360deg); } }
        @keyframes epIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

        .ep-page { display: flex; flex-direction: column; gap: 14px; animation: epIn 0.2s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .ep-back { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 10px 0 6px; margin-left: -6px; border: none; border-radius: 8px; background: transparent; color: var(--text-2); font-size: 13.5px; font-weight: 600; cursor: pointer; }
        .ep-back:hover { background: var(--border-soft); color: var(--text); }
        .ep-back:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

        .ep-head { display: flex; align-items: center; gap: 18px; padding: 20px 24px; background: var(--surface); border: 1px solid var(--border); border-radius: 18px; }
        .ep-name { display: flex; align-items: center; gap: 10px; margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.01em; color: var(--text); min-width: 0; }
        .ep-you { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px; background: var(--tint); color: var(--accent); letter-spacing: 0.02em; }
        .ep-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; margin-top: 8px; }
        .ep-meta-text { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; color: var(--text-muted); white-space: nowrap; }
        .ed-chip.ep-chip-warn { border: none; cursor: pointer; background: var(--warn-bg); color: var(--warn-text); font: inherit; font-size: 12px; font-weight: 600; }

        .ep-body { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: 16px; align-items: start; }
        .ep-nav { position: sticky; top: 16px; display: flex; flex-direction: column; gap: 2px; padding: 8px; background: var(--surface); border: 1px solid var(--border); border-radius: 18px; }
        .ep-nav-item { display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px; border: none; border-radius: 12px; background: transparent; text-align: left; cursor: pointer; transition: background 0.12s; font: inherit; }
        .ep-nav-item:hover { background: var(--surface-2); }
        .ep-nav-item:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
        .ep-nav-item[aria-current="page"] { background: var(--tint); }
        .ep-nav-icon { width: 32px; height: 32px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; background: var(--border-soft); color: var(--text-2); transition: background 0.12s, color 0.12s; }
        .ep-nav-item[aria-current="page"] .ep-nav-icon { background: var(--accent); color: var(--on-accent); }
        .ep-nav-label { display: flex; align-items: center; gap: 6px; font-size: 13.5px; font-weight: 600; color: var(--text); }
        .ep-nav-item[aria-current="page"] .ep-nav-label { color: var(--accent); }
        .ep-nav-hint { display: block; margin-top: 1px; font-size: 11.5px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ep-dot { width: 6px; height: 6px; border-radius: 50%; background: #F5A300; }

        .ep-content { min-width: 0; background: var(--surface); border: 1px solid var(--border); border-radius: 18px; }
        .ep-content-head { padding: 20px 24px 16px; border-bottom: 1px solid var(--border-soft); }
        .ep-content-head h2 { margin: 0; font-size: 17px; font-weight: 700; color: var(--text); }
        .ep-content-head p { margin: 3px 0 0; font-size: 13px; color: var(--text-muted); }
        .ep-content-body { padding: 22px 24px 26px; max-width: 760px; }
        .ep-roles { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
        @media (max-width: 1100px) { .ep-roles { grid-template-columns: 1fr; } }

        /* Below the fields it saves, never on top of them; only sticks (with room
           kept for it) while there are unsaved changes */
        .ep-savebar { position: relative; display: flex; align-items: center; justify-content: flex-end; gap: 10px; padding: 12px 24px; border-top: 1px solid var(--border-soft); background: var(--surface); border-radius: 0 0 18px 18px; }
        .ep-savebar[data-dirty] { position: sticky; bottom: 0; z-index: 5; box-shadow: 0 -6px 14px -10px rgba(28,26,20,.25); }
        .ep-savebar[data-dirty] { background: var(--warn-bg-soft); border-top-color: var(--warn-border); }
        .ep-savebar-msg { margin-right: auto; font-size: 12.5px; color: var(--text-muted); }
        .ep-savebar[data-dirty] .ep-savebar-msg { color: var(--warn-text); font-weight: 600; }

        /* Narrow screens: the section menu becomes a row of tabs you can swipe */
        @media (max-width: 860px) {
          .ep-body { grid-template-columns: 1fr; }
          .ep-nav { position: static; flex-direction: row; overflow-x: auto; scrollbar-width: none; padding: 6px; }
          .ep-nav::-webkit-scrollbar { display: none; }
          .ep-nav-item { width: auto; flex-shrink: 0; padding: 8px 12px; gap: 8px; }
          .ep-nav-hint { display: none; }
          .ep-nav-icon { width: 26px; height: 26px; }
          .ep-head { padding: 16px; gap: 14px; }
          .ep-name { font-size: 19px; }
          .ep-content-head, .ep-content-body { padding-left: 16px; padding-right: 16px; }
          .ep-savebar { padding: 12px 16px; }
        }

        .ed-input { transition: border-color 0.15s, box-shadow 0.15s, background 0.15s; }
        .ed-input:hover:not(:disabled) { border-color: var(--field-border) !important; }
        .ed-input:focus { background: var(--surface) !important; border-color: var(--accent) !important; box-shadow: 0 0 0 3px var(--tint); }
        .ed-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 560px) { .ed-grid { grid-template-columns: 1fr; } }

        .ed-chip { display: inline-flex; align-items: center; height: 22px; padding: 0 9px; border-radius: 11px; background: var(--border-soft); color: var(--text-2); font-size: 12px; font-weight: 600; white-space: nowrap; }

        .ed-photo { position: relative; cursor: pointer; flex-shrink: 0; border-radius: 50%; }
        .ed-photo-overlay { position: absolute; inset: 0; border-radius: 50%; background: rgba(28,26,20,0.55); display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.15s; }
        .ed-photo-overlay span { color: #fff; font-size: 10px; font-weight: 600; text-align: center; line-height: 1.3; }
        .ed-photo:hover .ed-photo-overlay { opacity: 1; }

        .ed-icon-btn { width: 34px; height: 34px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; border: 1px solid var(--border); border-radius: 10px; background: var(--surface); color: var(--text-muted); cursor: pointer; transition: background 0.15s, color 0.15s; }
        .ed-icon-btn:hover { background: var(--border-soft); color: var(--text); }
        .ed-icon-danger { border-color: transparent; background: transparent; width: 30px; height: 30px; }
        .ed-icon-danger:hover { background: var(--bad-bg); border-color: var(--bad-border); color: var(--bad-text); }

        .ed-btn { height: 40px; padding: 0 18px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: 1px solid var(--border); border-radius: 10px; background: var(--surface); color: var(--text-2); font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: background 0.15s, border-color 0.15s; }
        .ed-btn:hover { background: var(--border-soft); border-color: var(--field-border); }
        .ed-btn-primary { background: #FFC81E; border-color: #FFC81E; color: #1C1A14; }
        .ed-btn-primary:hover { background: #F5A300; border-color: #F5A300; }
        .ed-btn-sm { height: 32px; padding: 0 13px; font-size: 12.5px; border-radius: 9px; }
        .ed-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .ed-btn-primary:disabled:hover { background: #FFC81E; border-color: #FFC81E; }

        .ed-role { display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border: 1px solid var(--border); border-radius: 12px; background: var(--surface); cursor: pointer; transition: border-color 0.15s, background 0.15s; }
        .ed-role:hover { border-color: var(--field-border); }
        .ed-role[data-checked="true"] { border-color: var(--accent); background: var(--surface-2); box-shadow: 0 0 0 1px var(--accent) inset; }

        .ed-card { border: 1px solid var(--border); border-radius: 14px; overflow: hidden; background: var(--surface); }
        .ed-day { width: 34px; height: 34px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; transition: background 0.12s, border-color 0.12s, color 0.12s; }

        .ed-switch { position: relative; width: 36px; height: 20px; flex-shrink: 0; }
        .ed-switch input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; z-index: 1; }
        .ed-switch span { position: absolute; inset: 0; border-radius: 10px; background: var(--field-border); transition: background 0.15s; }
        .ed-switch span::after { content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.2); transition: transform 0.15s; }
        .ed-switch input:checked + span { background: var(--accent); }
        .ed-switch input:checked + span::after { transform: translateX(16px); }
        .ed-switch input:focus-visible + span { outline: 2px solid var(--accent); outline-offset: 2px; }

        input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0.45; cursor: pointer; }
        input[type="date"]::-webkit-calendar-picker-indicator:hover { opacity: 0.9; }
      `}</style>
    </div>
  );
}

export default EmployeeProfile;

// ─── SCHEDULE PANEL ──────────────────────────────────────────────────────────

function SchedulePanel({ employee }) {
  const [schedules, setSchedules] = useState([]);
  const [showForm,  setShowForm]  = useState(false);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    apiService.getEmployeeSchedules(employee.id)
      .then(res => {
        const list = res.data ?? res ?? [];
        if (Array.isArray(list) && list.length > 0) {
          setSchedules(list);
        } else {
          setSchedules([{
            id: null,
            from: employee.employmentStart
              ? new Date(employee.employmentStart).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : 'Start of employment',
            days: [true, true, true, true, true, false, false],
            hoursPerDay: 7.5,
          }]);
        }
      })
      .catch(e => {
        console.error('Load schedules failed:', e);
        setSchedules([{
          id: null,
          from: 'Start of employment',
          days: [true, true, true, true, true, false, false],
          hoursPerDay: 7.5,
        }]);
      })
      .finally(() => setLoading(false));
  }, [employee.id]);

  const addSchedule = async () => {
    try {
      const res = await apiService.createEmployeeSchedule(employee.id, {
        effectiveFrom: new Date().toISOString().split('T')[0],
        workDays:      [true, true, true, true, true, false, false],
        hoursPerDay:   FULL_DAY_HOURS,
      });
      const newSchedule = res.data ?? res;
      setSchedules(prev => [newSchedule, ...prev]);
      setShowForm(false);
    } catch (e) {
      console.error('Create schedule failed:', e);
    }
  };

  if (loading) return <PanelLoading />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.6 }}>
        Keep the schedule accurate so time-off deductions are right.
        Add a new schedule whenever their working pattern changes.
      </p>

      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          style={{
            width: '100%', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            fontSize: 13, fontWeight: 600, color: 'var(--accent)',
            border: '1.5px dashed var(--tint-strong)', borderRadius: 12,
            background: 'var(--surface)', cursor: 'pointer', transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
        >
          <Plus size={15} /> New work schedule
        </button>
      ) : (
        <div style={{
          border: '1px solid var(--tint-strong)', borderRadius: 12,
          padding: 16, display: 'flex', flexDirection: 'column', gap: 6,
          background: 'var(--surface-2)',
        }}>
          <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent)', margin: 0 }}>New schedule from today</p>
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: 0 }}>It becomes the current schedule. You can edit the days and hours after adding it.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={() => setShowForm(false)} className="ed-btn ed-btn-sm">Cancel</button>
            <button onClick={addSchedule} className="ed-btn ed-btn-sm ed-btn-primary">Add schedule</button>
          </div>
        </div>
      )}

      {schedules.slice(0, 1).map((s, i) => (
        <ScheduleCard
          key={s.id ?? i}
          schedule={s}
          employee={employee}
          dayLabels={DAY_LABELS}
          isCurrent
          onUpdate={updated => setSchedules(prev => prev.map((x, idx) => idx === 0 ? updated : x))}
          onDelete={() => setSchedules(prev => prev.filter((_, idx) => idx !== 0))}
        />
      ))}

      {schedules.length > 1 && (
        <PastSchedules
          schedules={schedules.slice(1)}
          employee={employee}
          dayLabels={DAY_LABELS}
          onUpdate={(updated, idx) => setSchedules(prev => prev.map((x, i) => i === idx + 1 ? updated : x))}
          onDelete={(idx) => setSchedules(prev => prev.filter((_, i) => i !== idx + 1))}
        />
      )}
    </div>
  );
}

function ScheduleCard({ schedule, employee, dayLabels, isCurrent, onUpdate, onDelete }) {
  const [editing,  setEditing]  = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [days,  setDays]  = useState(schedule.days ?? schedule.workDays ?? [true,true,true,true,true,false,false]);
  const [hours, setHours] = useState(schedule.hoursPerDay ?? schedule.hours_per_day ?? FULL_DAY_HOURS);

  const toggleDay  = idx => setDays(d => d.map((v, i) => i === idx ? !v : v));
  const activeDays = days.filter(Boolean).length;

  const handleSaveSchedule = async () => {
    try {
      if (schedule.id) {
        const res = await apiService.updateEmployeeSchedule(employee.id, schedule.id, {
          workDays:    days,
          hoursPerDay: parseFloat(hours),
        });
        onUpdate?.(res.data ?? res);
      } else {
        const res = await apiService.createEmployeeSchedule(employee.id, {
          effectiveFrom: schedule.effectiveFrom ?? new Date().toISOString().split('T')[0],
          workDays:      days,
          hoursPerDay:   parseFloat(hours),
        });
        onUpdate?.(res.data ?? res);
      }
      setEditing(false);
    } catch (e) {
      console.error('Save schedule failed:', e);
    }
  };

  const handleDelete = async () => {
    if (!schedule.id) { onDelete?.(); return; }
    setDeleting(true);
    try {
      await apiService.deleteEmployeeSchedule(employee.id, schedule.id);
      onDelete?.();
    } catch (e) {
      console.error('Delete schedule failed:', e);
      setDeleting(false);
    }
  };

  return (
    <div className="ed-card" style={isCurrent ? { borderColor: 'var(--tint-strong)' } : undefined}>
      <div style={{
        padding: '10px 10px 10px 16px',
        background: isCurrent ? 'var(--surface-2)' : 'var(--surface-2)',
        borderBottom: '1px solid var(--border-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>From</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{schedule.from ?? schedule.effectiveFrom}</span>
          {isCurrent && (
            <span className="ed-chip" style={{ height: 20, fontSize: 11, background: 'var(--tint)', color: 'var(--accent)' }}>Current</span>
          )}
        </div>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="ed-icon-btn ed-icon-danger"
          title="Delete schedule"
          aria-label="Delete schedule"
        >
          {deleting ? <Loader2 size={14} style={{ animation: 'edSpin 1s linear infinite' }} /> : <Trash2 size={14} />}
        </button>
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 5 }} role="group" aria-label="Working days">
            {dayLabels.map((d, j) => (
              <button
                key={j}
                onClick={() => editing && toggleDay(j)}
                className="ed-day"
                aria-pressed={days[j]}
                style={{
                  border: `1px solid ${days[j] ? 'var(--accent)' : 'var(--border)'}`,
                  background: days[j] ? 'var(--accent)' : 'var(--surface)',
                  color: days[j] ? 'var(--on-accent)' : 'var(--text-muted)',
                  cursor: editing ? 'pointer' : 'default',
                }}
              >
                {d}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
            <b style={{ color: 'var(--text)' }}>{activeDays}</b> days · <b style={{ color: 'var(--text)' }}>{hours}h</b> a day
          </span>
        </div>

        {editing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--surface-2)' }}>
            <label style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 500, whiteSpace: 'nowrap' }}>Hours per day</label>
            <input
              type="number" min={1} max={24} step={0.5} value={hours}
              onChange={e => setHours(e.target.value)}
              className="ed-input"
              style={{ width: 72, height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 13.5, color: 'var(--text)', padding: '0 10px', outline: 'none', textAlign: 'right', boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>hours</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>Click days above to toggle</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {editing ? (
            <>
              <button onClick={handleSaveSchedule} className="ed-btn ed-btn-sm ed-btn-primary">Save</button>
              <button onClick={() => setEditing(false)} className="ed-btn ed-btn-sm">Cancel</button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="ed-btn ed-btn-sm">
              Edit schedule
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PastSchedules({ schedules, employee, dayLabels, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{ background: 'none', border: 'none', padding: '4px 0', fontSize: 13, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <ChevronRight size={15} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
        {open ? 'Hide' : 'Show'} {schedules.length} previous schedule{schedules.length > 1 ? 's' : ''}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {schedules.map((s, i) => (
            <ScheduleCard
              key={s.id ?? i}
              schedule={s}
              employee={employee}
              dayLabels={DAY_LABELS}
              isCurrent={false}
              onUpdate={updated => onUpdate?.(updated, i)}
              onDelete={() => onDelete?.(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ALLOWANCE PANEL ─────────────────────────────────────────────────────────
// Figures come from the server's leave engine (GET /employees/:id/allowances
// → balance), so they match the calendar, the dashboard and the mobile app.
// Two modes, from the person's leave policy:
//   • per employee — allowance, carry forward and time in lieu are typed in
//     here (the original behaviour)
//   • policy       — the policy works the allowance out (fixed or accruing,
//     days or hours, carry-over rules); here you can see the breakdown and
//     set a one-off override, opening balance or adjustment.

const KIND_LABEL = { opening: 'Opening balance', adjustment: 'Adjustment', toil: 'Time in lieu' };

function AllowancePanel({ employee }) {
  const { currentUser } = useApp();
  const canEdit = ['admin', 'super_admin'].includes(currentUser?.role);
  const currentYear = new Date().getFullYear();

  const [year,        setYear]        = useState(currentYear);
  const [data,        setData]        = useState(null);   // GET /allowances response
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState(false);
  const [policies,    setPolicies]    = useState([]);
  const [policyId,    setPolicyId]    = useState('');     // '' = department / company default
  const [phOptOut,    setPhOptOut]    = useState(false);  // public holidays don't apply to this person
  const [adjustments, setAdjustments] = useState([]);
  const [saveStatus,  setSaveStatus]  = useState('idle'); // idle | saving | saved | error

  // Per-employee mode fields (hours, like the API)
  const [unit,         setUnit]         = useState('days');
  const [allowance,    setAllowance]    = useState(0);
  const [carryForward, setCarryForward] = useState(0);
  const [timeInLieu,   setTimeInLieu]   = useState(0);

  // Policy mode overrides (in the policy's unit; '' = use the policy)
  const [entOverride,   setEntOverride]   = useState('');
  const [carryOverride, setCarryOverride] = useState('');

  const [adjForm, setAdjForm] = useState({ date: new Date().toISOString().slice(0, 10), kind: 'adjustment', amount: '', note: '' });
  const [adjBusy, setAdjBusy] = useState(false);

  const balance     = data?.balance ?? null;
  const policy      = data?.policy ?? null;
  const perEmployee = policy?.allowanceSource === 'per_employee';
  const hpd         = balance?.hoursPerDay || FULL_DAY_HOURS;

  async function load(y = year) {
    setLoading(true);
    setLoadError(false);
    try {
      const [allowRes, adjRes] = await Promise.all([
        apiService.getEmployeeAllowance(employee.id, y),
        apiService.getBalanceAdjustments(employee.id).catch(() => null),
      ]);
      const d = allowRes?.data ?? allowRes;
      setData(d);
      setUnit(d?.displayUnit ?? 'days');
      setAllowance(num(d?.allowanceHours, 0));
      setCarryForward(num(d?.carryForwardHours, 0));
      setTimeInLieu(num(d?.overrides?.timeInLieuHours ?? d?.timeInLieuHours, 0));
      const o = d?.overrides;
      const sameUnit = !o?.overrideUnit || o.overrideUnit === d?.balance?.unit;
      setEntOverride(o?.entitlementOverride != null && sameUnit ? String(o.entitlementOverride) : '');
      setCarryOverride(o?.carryOverOverride != null && sameUnit ? String(o.carryOverOverride) : '');
      setAdjustments(Array.isArray(adjRes?.data) ? adjRes.data : []);
    } catch (e) {
      console.error('Load allowance failed:', e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(currentYear);
    apiService.getLeavePolicies()
      .then(res => setPolicies(Array.isArray(res?.data) ? res.data.filter(p => p.isActive !== false) : []))
      .catch(() => {});
    apiService.getEmployeeProfile(employee.id)
      .then(res => {
        const prof = res?.data ?? res;
        setPolicyId(prof?.leavePolicyId ?? '');
        setPhOptOut(!!prof?.publicHolidayOptOut);
      })
      .catch(() => {});
  }, [employee.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (state) => {
    setSaveStatus(state);
    setTimeout(() => setSaveStatus('idle'), state === 'error' ? 3000 : 2500);
  };

  async function changePolicy(id) {
    setPolicyId(id);
    try {
      await apiService.updateEmployeeProfile(employee.id, { leavePolicyId: id || null });
      await load(year);
    } catch (e) {
      console.error('Change policy failed:', e);
      flash('error');
    }
  }

  async function changePhOptOut(optOut) {
    setPhOptOut(optOut);
    try {
      await apiService.updateEmployeeProfile(employee.id, { publicHolidayOptOut: optOut });
      await load(year);
    } catch (e) {
      console.error('Change public holidays failed:', e);
      setPhOptOut(!optOut);
      flash('error');
    }
  }

  async function handleSave() {
    setSaveStatus('saving');
    try {
      const leaveYear = balance?.period?.leaveYear ?? year;
      if (perEmployee) {
        await apiService.upsertEmployeeAllowance(employee.id, {
          leaveYear,
          allowanceHours:        allowance,
          carryForwardHours:     carryForward,
          timeInLieuHours:       timeInLieu,
          displayUnit:           unit,
        });
      } else {
        await apiService.upsertEmployeeAllowance(employee.id, {
          leaveYear,
          entitlementOverride: entOverride === '' ? null : Number(entOverride),
          carryOverOverride:   carryOverride === '' ? null : Number(carryOverride),
          overrideUnit:        balance?.unit ?? 'days',
          timeInLieuHours:     timeInLieu,
        });
      }
      await load(year);
      flash('saved');
    } catch (e) {
      console.error('Save allowance failed:', e);
      flash('error');
    }
  }

  async function addAdjustment() {
    const amount = parseFloat(adjForm.amount);
    if (isNaN(amount) || !adjForm.date) return;
    setAdjBusy(true);
    try {
      await apiService.addBalanceAdjustment(employee.id, { ...adjForm, amount, unit: balance?.unit ?? 'days' });
      setAdjForm(f => ({ ...f, amount: '', note: '' }));
      await load(year);
    } catch (e) {
      console.error('Add adjustment failed:', e);
      flash('error');
    } finally {
      setAdjBusy(false);
    }
  }

  async function removeAdjustment(id) {
    if (!window.confirm('Delete this adjustment?')) return;
    try {
      await apiService.deleteBalanceAdjustment(employee.id, id);
      await load(year);
    } catch (e) {
      console.error('Delete adjustment failed:', e);
    }
  }

  if (loading && !data) return <PanelLoading />;
  if (loadError && !data) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 12, background: 'var(--bad-bg)', border: '1px solid var(--bad-border)', color: 'var(--bad-text)', fontSize: 13 }}>
        <AlertCircle size={15} /> Couldn't load the allowance. <button className="ed-btn ed-btn-sm" onClick={() => load(year)}>Try again</button>
      </div>
    );
  }

  // Per-employee: the "Track allowance in" choice. Policy-based: set by the policy.
  const inDays    = (perEmployee ? unit : (balance?.unit ?? unit)) === 'days';
  const unitLabel = inDays ? 'Days' : 'Hours';
  const show      = (b) => (b ? (inDays ? fmtDays(b.days) : fmtHours(b.hours)) : '—');
  const noBalance = !!balance?.noBalanceTracking;
  const accruing  = balance?.method === 'accrual' && !perEmployee;
  const overdrawn = !noBalance && (inDays ? balance?.available?.days : balance?.available?.hours) < 0;

  // Per-employee inputs are entered in the chosen unit, stored in hours
  const toDisplay   = val => inDays ? parseFloat((Number(val) / hpd).toFixed(2)) : Number(val);
  const fromDisplay = val => {
    const n = parseFloat(val);
    if (isNaN(n)) return 0;
    return inDays ? parseFloat((n * hpd).toFixed(2)) : n;
  };

  const numInputStyle = {
    width: 92, textAlign: 'right', height: 34, padding: '0 10px',
    borderRadius: 8, border: '1px solid var(--border)',
    background: 'var(--surface-2)', fontSize: 13.5, color: 'var(--text)', outline: 'none',
    boxSizing: 'border-box',
  };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  const defaultPolicy = policies.find(p => p.isDefault);
  const carried = balance?.carriedOver;

  const activeUnit = inDays ? 'days' : 'hours';
  const unitLocked = !perEmployee || !canEdit;

  // Per-employee mode: saved straight away (only the unit changes)
  async function changeUnit(u) {
    if (unitLocked || u === unit) return;
    const prev = unit;
    setUnit(u);
    try {
      await apiService.upsertEmployeeAllowance(employee.id, {
        leaveYear: balance?.period?.leaveYear ?? year,
        displayUnit: u,
      });
      await load(year);
    } catch (e) {
      console.error('Change unit failed:', e);
      setUnit(prev);
      flash('error');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Track allowance in — first, as everything below is shown in this unit */}
      <div className="ed-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
          <span>
            <span style={{ display: 'block', fontSize: 13.5, color: 'var(--text)', fontWeight: 600 }}>Track allowance in</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {perEmployee
                ? `Everything below is shown in ${activeUnit}.`
                : `Set by their leave policy${policy?.name ? ` (${policy.name})` : ''}.`}
            </span>
          </span>
          <div style={{ display: 'flex', gap: 2, padding: 3, background: 'var(--border-soft)', borderRadius: 9, opacity: unitLocked ? 0.7 : 1 }} role="group" aria-label="Allowance unit">
            {['hours', 'days'].map(u => (
              <button
                key={u}
                type="button"
                onClick={() => changeUnit(u)}
                aria-pressed={activeUnit === u}
                disabled={unitLocked}
                title={!perEmployee ? 'Change the unit on the leave policy' : undefined}
                style={{
                  height: 28, padding: '0 12px', border: 'none', borderRadius: 7, cursor: unitLocked ? 'default' : 'pointer',
                  fontSize: 12.5, fontWeight: activeUnit === u ? 600 : 500, textTransform: 'capitalize',
                  background: activeUnit === u ? 'var(--surface)' : 'transparent', color: activeUnit === u ? 'var(--accent)' : 'var(--text-2)',
                  boxShadow: activeUnit === u ? '0 1px 2px rgba(28,26,20,0.10)' : 'none',
                }}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Policy */}
      <div className="ed-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
          <span>
            <span style={{ display: 'block', fontSize: 13.5, color: 'var(--text)', fontWeight: 600 }}>Leave policy</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {data?.policySource === 'department' ? "From their department's policy" : data?.policySource === 'default' ? 'Company default' : 'Set for this person'}
              {policy && ` · ${perEmployee ? 'allowance set per person' : accruing ? `accrues in ${policy.unit}` : `fixed allowance in ${policy.unit}`}`}
            </span>
          </span>
          {canEdit && policies.length > 0 ? (
            <select className="ed-input" style={{ ...selectStyle, width: 'auto', minWidth: 220 }} value={policyId} onChange={e => changePolicy(e.target.value)} aria-label="Leave policy">
              <option value="">Default{defaultPolicy ? ` (${defaultPolicy.name})` : ''}</option>
              {policies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          ) : (
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{policy?.name ?? '—'}</span>
          )}
        </div>
      </div>

      {/* Public holidays for this person (on by default). When on they're days
          off and never come off the allowance; off = ordinary working days. */}
      <div className="ed-card">
        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', cursor: canEdit ? 'pointer' : 'default' }}>
          <span>
            <span style={{ display: 'block', fontSize: 13.5, color: 'var(--text)', fontWeight: 600 }}>Public holidays apply</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>
              {data?.observesPublicHolidays === false && !phOptOut
                ? "Their leave policy doesn't include public holidays."
                : phOptOut
                  ? "Off: public holidays are ordinary working days for them — not shown as holidays, and leave on those days comes off their allowance."
                  : `On: ${data?.holidayLocationName ? `${data.holidayLocationName} ` : ''}public holidays are days off for them and never come off their allowance.`}
            </span>
          </span>
          <span className="ed-switch">
            <input type="checkbox" checked={!phOptOut} disabled={!canEdit} onChange={e => changePhOptOut(!e.target.checked)} aria-label="Public holidays apply to this person" />
            <span />
          </span>
        </label>
      </div>

      {noBalance ? (
        <div style={{ padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 13, color: 'var(--text-2)' }}>
          Their department doesn't track allowance — leave shows on the calendar but isn't taken off a balance.
        </div>
      ) : (
        <>
          {/* Headline figures */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { label: accruing ? 'Accrued + carried' : 'Total',  val: show(balance?.total), color: 'var(--text)' },
              { label: 'Used',      val: show(balance?.used),      color: 'var(--bad-text)' },
              { label: 'Available', val: show(balance?.available), color: overdrawn ? 'var(--bad-text)' : 'var(--ok-text)' },
            ].map(s => (
              <div key={s.label} style={{ padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                <p style={{ margin: 0, fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)' }}>{s.label}</p>
                <p style={{ margin: '4px 0 0', fontSize: 19, fontWeight: 700, color: s.color }}>{s.val}</p>
              </div>
            ))}
          </div>
          <p style={{ margin: '-6px 2px 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {balance?.pending?.hours > 0 && (
              <span style={{ color: 'var(--warn-text)', fontWeight: 600 }}>{show(balance.pending)} waiting for approval. </span>
            )}
            {accruing && <>Builds up as they work — {show(balance?.projected)} expected by the end of the period after booked leave. </>}
            {inDays && <>A working day is {fmtHours(hpd)} on their current schedule.</>}
          </p>
        </>
      )}

      {/* Period breakdown */}
      <div className="ed-card">
        <div style={{ padding: 12, borderBottom: '1px solid var(--border-soft)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select
            className="ed-input"
            style={{ ...selectStyle, background: 'var(--surface)', fontWeight: 600, width: 'auto' }}
            value={year}
            onChange={e => { const y = Number(e.target.value); setYear(y); load(y); }}
            aria-label="Leave year"
          >
            {/* Two years back, the current leave year and the next five */}
            {Array.from({ length: 8 }, (_, i) => currentYear - 2 + i).map(y => (
              <option key={y} value={y}>{y === currentYear ? 'Current leave year' : `Leave year ${y}`}</option>
            ))}
          </select>
          {balance?.period?.label && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{balance.period.label}</span>}
          {loading && <Loader2 size={14} style={{ animation: 'edSpin 1s linear infinite', color: 'var(--text-muted)' }} />}
        </div>

        {!noBalance && balance && (
          <>
            <ARow label="Allowance" right={unitLabel} muted />
            {balance.opening && <ARow label={`Opening balance (${fmtDate(balance.opening.date)})`} right={show(balance.opening)} />}
            {(carried?.hours ?? 0) !== 0 && (
              <ARow
                label={carried.expiresOn
                  ? `Carried over (${carried.expired?.hours > 0 ? `${show(carried.expired)} expired` : `use by ${fmtDate(carried.expiresOn)}`})`
                  : 'Carried over'}
                right={show(carried)}
              />
            )}
            {accruing ? (
              <>
                <ARow label="Accrued so far" right={show(balance.accrued)} />
                <ARow label="Accrues over the full period" right={show(balance.entitlement)} />
              </>
            ) : (
              <ARow label="Allowance for the period" right={show(balance.entitlement)} />
            )}
            {(balance.adjustments?.hours ?? 0) !== 0 && <ARow label="Time in lieu and adjustments" right={show(balance.adjustments)} />}
            <ARow label="Total" right={show(balance.total)} bold />
            <ARow label="Deductions" right={unitLabel} muted />
            <ARow label="Leave booked (approved)" right={show(balance.used)} red />
            {balance.pending?.hours > 0 && <ARow label="Waiting for approval" right={show(balance.pending)} />}
            <ARow label={`${unitLabel} available`} right={show(balance.available)} bold last />
          </>
        )}
      </div>

      {/* Editing */}
      {canEdit && !noBalance && (
        perEmployee ? (
          <div className="ed-card">
            <ARow label="Allowance">
              <input type="number" className="ed-input" min={0} step={inDays ? 0.5 : 1} value={toDisplay(allowance)} onChange={e => setAllowance(fromDisplay(e.target.value))} style={numInputStyle} />
            </ARow>
            <ARow label="Carry forward">
              <input type="number" className="ed-input" step={inDays ? 0.5 : 1} value={toDisplay(carryForward)} onChange={e => setCarryForward(fromDisplay(e.target.value))} style={numInputStyle} />
            </ARow>
            <ARow label="Time in lieu" last>
              <input type="number" className="ed-input" step={0.5} value={toDisplay(timeInLieu)} onChange={e => setTimeInLieu(fromDisplay(e.target.value))} style={numInputStyle} />
            </ARow>
          </div>
        ) : (
          <div className="ed-card">
            <ARow label={`Overrides for this period (${unitLabel.toLowerCase()})`} right="" muted />
            {!accruing && (
              <ARow label="Custom allowance">
                <input
                  type="number" className="ed-input" min={0} step={inDays ? 0.5 : 1}
                  placeholder={String(balance?.entitlement ? (inDays ? balance.entitlement.days : balance.entitlement.hours) : '')}
                  value={entOverride} onChange={e => setEntOverride(e.target.value)} style={numInputStyle}
                  aria-label="Custom allowance (blank = from the policy)"
                />
              </ARow>
            )}
            <ARow label="Carried over">
              <input
                type="number" className="ed-input" step={inDays ? 0.5 : 1}
                placeholder="Calculated"
                value={carryOverride} onChange={e => setCarryOverride(e.target.value)} style={numInputStyle}
                aria-label="Carried over (blank = calculated)"
              />
            </ARow>
            <ARow label="Time in lieu (hours)" last>
              <input type="number" className="ed-input" step={0.5} value={timeInLieu} onChange={e => setTimeInLieu(num(e.target.value, 0))} style={numInputStyle} />
            </ARow>
            <p style={{ margin: 0, padding: '10px 16px 12px', fontSize: 12, color: 'var(--text-muted)', borderTop: '1px solid var(--border-soft)' }}>
              Leave a box empty to use the figure worked out from the policy.
            </p>
          </div>
        )
      )}

      {saveStatus === 'error' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderRadius: 10,
          background: 'var(--bad-bg)', border: '1px solid var(--bad-border)',
          fontSize: 13, color: 'var(--bad-text)',
        }}>
          <AlertCircle size={15} /> Failed to save. Please try again.
        </div>
      )}

      {canEdit && !noBalance && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            className="ed-btn ed-btn-primary"
            style={saveStatus === 'saved' ? { background: 'var(--ok-text)', borderColor: 'var(--ok-text)', color: 'var(--on-accent)' } : undefined}
          >
            {saveStatus === 'saving'
              ? <><Loader2 size={14} style={{ animation: 'edSpin 1s linear infinite' }} /> Saving…</>
              : saveStatus === 'saved'
                ? <><Check size={14} /> Saved</>
                : 'Save changes'}
          </button>
        </div>
      )}

      {/* Opening balances and adjustments (policy-based allowances) */}
      {canEdit && !noBalance && !perEmployee && (
        <div className="ed-card">
          <ARow label="Opening balances and adjustments" right="" muted />
          {adjustments.length === 0 && (
            <p style={{ margin: 0, padding: '12px 16px', fontSize: 12.5, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-soft)' }}>
              None yet. Moving from another system? Add an opening balance as at the date you start using Clock-out.
            </p>
          )}
          {adjustments.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border-soft)', fontSize: 13 }}>
              <span style={{ width: 92, color: 'var(--text-muted)' }}>{fmtDate(a.date)}</span>
              <span style={{ flex: 1, minWidth: 0, color: 'var(--text)' }}>
                {KIND_LABEL[a.kind] ?? a.kind}
                {a.note && <span style={{ color: 'var(--text-muted)' }}> · {a.note}</span>}
              </span>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{Number(a.amount) > 0 && a.kind !== 'opening' ? '+' : ''}{Number(a.amount)} {a.unit === 'hours' ? 'h' : 'd'}</span>
              <button className="ed-icon-btn ed-icon-danger" onClick={() => removeAdjustment(a.id)} aria-label="Delete adjustment"><Trash2 size={14} /></button>
            </div>
          ))}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 12, alignItems: 'center' }}>
            <input type="date" className="ed-input" style={{ ...inputStyle, width: 150, height: 34 }} value={adjForm.date} onChange={e => setAdjForm(f => ({ ...f, date: e.target.value }))} aria-label="Date" />
            <select className="ed-input" style={{ ...selectStyle, width: 150, height: 34 }} value={adjForm.kind} onChange={e => setAdjForm(f => ({ ...f, kind: e.target.value }))} aria-label="Type">
              <option value="adjustment">Adjustment</option>
              <option value="toil">Time in lieu</option>
              <option value="opening">Opening balance</option>
            </select>
            <input type="number" step={0.5} className="ed-input" style={{ ...numInputStyle, height: 34 }} placeholder={inDays ? 'Days' : 'Hours'} value={adjForm.amount} onChange={e => setAdjForm(f => ({ ...f, amount: e.target.value }))} aria-label="Amount" />
            <input className="ed-input" style={{ ...inputStyle, flex: 1, minWidth: 140, height: 34 }} placeholder="Note (optional)" value={adjForm.note} onChange={e => setAdjForm(f => ({ ...f, note: e.target.value }))} aria-label="Note" />
            <button className="ed-btn ed-btn-sm" onClick={addAdjustment} disabled={adjBusy || adjForm.amount === ''}>
              {adjBusy ? <Loader2 size={13} style={{ animation: 'edSpin 1s linear infinite' }} /> : <Plus size={13} />} Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// parseFloat with a fallback — DECIMAL columns arrive as strings
function num(v, fallback) {
  const n = parseFloat(v);
  return isNaN(n) ? fallback : n;
}

// ─── INVITE PANEL ────────────────────────────────────────────────────────────

function InvitePanel({ employee }) {
  const [status,   setStatus]   = useState('idle'); // idle | sending | sent | error
  const [inviteUrl, setInviteUrl] = useState(null);
  const [copied,   setCopied]   = useState(false);

  const fullName = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || employee.name || 'this user';
  const isVerified = employee.isEmailVerified;

  async function handleSendInvite() {
    setStatus('sending');
    try {
      const res = await apiService.sendUserInvite(employee.id);
      setInviteUrl(res?.data?.activationUrl ?? null);
      setStatus('sent');
    } catch (e) {
      console.error('Invite failed:', e);
      setStatus('error');
    }
  }

  function copyUrl() {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const tone = isVerified
    ? { bg: 'var(--ok-bg)', border: 'var(--ok-border)', iconBg: 'var(--ok-bg)', fg: 'var(--ok-text)', Icon: Check }
    : { bg: 'var(--warn-bg-soft)', border: 'var(--warn-border)', iconBg: 'var(--warn-bg)', fg: 'var(--warn-text)', Icon: AlertCircle };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Status */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 16px', borderRadius: 14,
        background: tone.bg, border: `1px solid ${tone.border}`,
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: 11, flexShrink: 0,
          background: tone.iconBg, color: tone.fg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <tone.Icon size={18} strokeWidth={2.4} />
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            {isVerified ? 'Account activated' : 'Invite pending'}
          </p>
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '2px 0 0', lineHeight: 1.45 }}>
            {isVerified
              ? `${fullName} has accepted their invite and logged in.`
              : `${fullName} hasn't activated their account yet.`}
          </p>
        </div>
      </div>

      {/* Sent confirmation */}
      {status === 'sent' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '11px 14px', borderRadius: 10,
          background: 'var(--ok-bg)', border: '1px solid var(--ok-border)',
          fontSize: 13, color: 'var(--ok-text)', fontWeight: 500,
        }}>
          <Check size={15} /> Invite sent to {employee.email}
        </div>
      )}

      {status === 'error' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '11px 14px', borderRadius: 10,
          background: 'var(--bad-bg)', border: '1px solid var(--bad-border)',
          fontSize: 13, color: 'var(--bad-text)',
        }}>
          <AlertCircle size={15} /> Failed to send invite. Please try again.
        </div>
      )}

      {/* Activation URL (dev only) */}
      {inviteUrl && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', margin: 0 }}>
            Activation link
          </p>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 6px 6px 12px', borderRadius: 10,
            background: 'var(--surface-2)', border: '1px solid var(--border)',
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {inviteUrl}
            </span>
            <button
              onClick={copyUrl}
              className="ed-btn ed-btn-sm"
              style={copied ? { color: 'var(--ok-text)', borderColor: 'var(--ok-border)', background: 'var(--ok-bg)' } : { color: 'var(--accent)' }}
            >
              {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
            </button>
          </div>
        </div>
      )}

      {/* Send / Resend button */}
      <button
        onClick={handleSendInvite}
        disabled={status === 'sending'}
        className="ed-btn ed-btn-primary"
        style={{ width: '100%', height: 44 }}
      >
        {status === 'sending' ? (
          <><Loader2 size={15} style={{ animation: 'edSpin 1s linear infinite' }} /> Sending…</>
        ) : status === 'sent' ? (
          <><Mail size={15} /> Resend invite</>
        ) : (
          <><Mail size={15} /> {isVerified ? 'Resend welcome email' : 'Send invite email'}</>
        )}
      </button>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, textAlign: 'center', lineHeight: 1.6 }}>
        The link expires after 7 days. You can resend it at any time.
      </p>
    </div>
  );
}

// ─── MEMBERSHIP (deactivate / archive) ───────────────────────────────────────
// Deactivated and archived people can't sign in and don't count towards the
// subscription; their leave history stays on the calendar and in reports.

const STATUS_COPY = {
  active:      { title: 'Active member', body: 'Can sign in and book leave. Counts towards your subscription.' },
  deactivated: { title: 'Deactivated', body: "Can't sign in and doesn't count towards your subscription. Reactivate them at any time." },
  archived:    { title: 'Archived', body: "Has left the company. Their leave history is kept, they can't sign in and they don't count towards your subscription." },
};

function MembershipSection({ employee, status, onChange }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const name = employee.firstName || employee.name || 'this person';

  async function setStatus(next) {
    const verb = next === 'active' ? 'Reactivate' : next === 'archived' ? 'Archive' : 'Deactivate';
    if (next !== 'active' && !window.confirm(`${verb} ${name}? They'll be signed out and won't be able to sign in.`)) return;
    setBusy(next);
    setError('');
    try {
      await apiService.updateUserStatus(employee.id, next);
      onChange(next);
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't change their status. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  const copy = STATUS_COPY[status] ?? STATUS_COPY.active;
  return (
    <Section icon={Power} title="Membership">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
        <span style={{ flex: 1, minWidth: 200 }}>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: status === 'active' ? 'var(--text)' : 'var(--bad-text)' }}>{copy.title}</span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>{copy.body}</span>
        </span>
        {status === 'active' ? (
          <>
            <button type="button" className="ed-btn ed-btn-sm" disabled={!!busy} onClick={() => setStatus('deactivated')}>
              {busy === 'deactivated' ? <Loader2 size={13} style={{ animation: 'edSpin 1s linear infinite' }} /> : null} Deactivate
            </button>
            <button type="button" className="ed-btn ed-btn-sm" disabled={!!busy} onClick={() => setStatus('archived')}>
              {busy === 'archived' ? <Loader2 size={13} style={{ animation: 'edSpin 1s linear infinite' }} /> : null} Archive (left)
            </button>
          </>
        ) : (
          <button type="button" className="ed-btn ed-btn-sm ed-btn-primary" disabled={!!busy} onClick={() => setStatus('active')}>
            {busy ? <Loader2 size={13} style={{ animation: 'edSpin 1s linear infinite' }} /> : null} Reactivate
          </button>
        )}
      </div>
      {error && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--bad-text)' }}>{error}</p>}
    </Section>
  );
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function Section({ icon: Icon, title, children }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
        <span style={{ width: 26, height: 26, borderRadius: 8, background: 'var(--tint)', color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} />
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}

function PanelLoading() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)', padding: '8px 2px' }}>
      <Loader2 size={15} style={{ animation: 'edSpin 1s linear infinite' }} /> Loading…
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function SaveButton({ onClick, saved }) {
  return (
    <button
      onClick={onClick}
      className="ed-btn ed-btn-primary"
      style={saved ? { background: 'var(--ok-text)', borderColor: 'var(--ok-text)', color: 'var(--on-accent)' } : undefined}
    >
      {saved ? <><Check size={14} /> Saved</> : 'Save changes'}
    </button>
  );
}

function ARow({ label, right, children, bold, muted, red, last }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      minHeight: muted ? 34 : 46, padding: '0 16px',
      borderBottom: last ? 'none' : '1px solid var(--border-soft)',
      background: bold ? 'var(--surface-2)' : muted ? 'var(--surface-2)' : 'var(--surface)',
    }}>
      <span style={{
        fontSize: muted ? 11 : 13.5,
        fontWeight: bold ? 700 : muted ? 700 : 400,
        color: muted ? 'var(--text-muted)' : 'var(--text)',
        textTransform: muted ? 'uppercase' : 'none',
        letterSpacing: muted ? '0.06em' : 0,
      }}>
        {label}
      </span>
      {children ?? (
        <span style={{
          fontSize: muted ? 11 : 13.5,
          fontWeight: bold ? 700 : muted ? 700 : 500,
          color: red ? 'var(--bad-text)' : muted ? 'var(--text-muted)' : 'var(--text)',
          textTransform: muted ? 'uppercase' : 'none',
          letterSpacing: muted ? '0.06em' : 0,
        }}>
          {right}
        </span>
      )}
    </div>
  );
}